/**
 * Accounts & Ledger Export Utility (Excel & PDF)
 * Maruti Fire System
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { format, parseISO, isValid } from 'date-fns';

export type AccountType =
  | 'Customer'
  | 'Vendor'
  | 'Bank'
  | 'Cash'
  | 'Employee'
  | 'Partner'
  | 'Loan'
  | 'Expense'
  | 'Other';

export type AccountStatus = 'active' | 'inactive';
export type EntryType = 'credit' | 'debit';

export interface Account {
  id: string;
  accountName: string;
  accountType: AccountType;
  contactPerson: string;
  phone: string;
  email: string;
  openingBalance: number;
  currentBalance: number;
  status: AccountStatus;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export interface LedgerEntry {
  id: string;
  accountId: string;
  entryType: EntryType;
  amount: number;
  entryDate: string;
  category: string;
  paymentMode: string;
  referenceNo: string;
  notes: string;
  createdAt: string;
}

const formatMoney = (amount: number): string =>
  new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);

const formatDateStr = (dateStr: string): string => {
  if (!dateStr) return '';
  const parsed = parseISO(dateStr);
  return isValid(parsed) ? format(parsed, 'dd-MM-yyyy') : dateStr;
};

/**
 * Export Accounts and Ledger Entries to an Excel workbook (.xlsx)
 */
export const exportLedgerToExcel = (
  accounts: Account[],
  entries: LedgerEntry[],
  accountMap: Map<string, Account>,
  filenamePrefix: string = 'Full_Ledger'
) => {
  const wb = XLSX.utils.book_new();

  // 1. Ledger Entries Sheet
  const ledgerRows = entries.map((entry, index) => {
    const account = accountMap.get(entry.accountId);
    return {
      'S.No': index + 1,
      Date: formatDateStr(entry.entryDate),
      'Account Name': account?.accountName || 'N/A',
      'Account Type': account?.accountType || 'N/A',
      'Entry Type': entry.entryType.toUpperCase(),
      'Category': entry.category,
      'Payment Mode': entry.paymentMode,
      'Ref / Chq No': entry.referenceNo || '',
      'Amount (Rs)': entry.amount,
      'Notes': entry.notes || '',
    };
  });

  // Calculate totals
  const totalCredit = entries.reduce(
    (sum, entry) => (entry.entryType === 'credit' ? sum + entry.amount : sum),
    0
  );
  const totalDebit = entries.reduce(
    (sum, entry) => (entry.entryType === 'debit' ? sum + entry.amount : sum),
    0
  );

  // Append Summary Row to Ledger
  ledgerRows.push({
    'S.No': 'TOTAL' as any,
    Date: '',
    'Account Name': '',
    'Account Type': '',
    'Entry Type': `CREDIT: Rs ${formatMoney(totalCredit)}`,
    'Category': `DEBIT: Rs ${formatMoney(totalDebit)}`,
    'Payment Mode': `NET: Rs ${formatMoney(totalCredit - totalDebit)}`,
    'Ref / Chq No': '',
    'Amount (Rs)': totalCredit - totalDebit,
    'Notes': '',
  });

  const wsLedger = XLSX.utils.json_to_sheet(ledgerRows);
  wsLedger['!cols'] = [
    { wch: 6 },
    { wch: 12 },
    { wch: 25 },
    { wch: 15 },
    { wch: 12 },
    { wch: 15 },
    { wch: 16 },
    { wch: 16 },
    { wch: 16 },
    { wch: 30 },
  ];
  XLSX.utils.book_append_sheet(wb, wsLedger, 'Ledger Entries');

  // 2. Accounts Summary Sheet
  const accountRows = accounts.map((account, index) => ({
    'S.No': index + 1,
    'Account Name': account.accountName,
    'Type': account.accountType,
    'Contact Person': account.contactPerson || '',
    'Phone': account.phone || '',
    'Email': account.email || '',
    'Status': account.status.toUpperCase(),
    'Opening Balance (Rs)': account.openingBalance,
    'Current Balance (Rs)': account.currentBalance,
    'Notes': account.notes || '',
  }));

  const wsAccounts = XLSX.utils.json_to_sheet(accountRows);
  wsAccounts['!cols'] = [
    { wch: 6 },
    { wch: 25 },
    { wch: 15 },
    { wch: 20 },
    { wch: 15 },
    { wch: 25 },
    { wch: 10 },
    { wch: 20 },
    { wch: 20 },
    { wch: 30 },
  ];
  XLSX.utils.book_append_sheet(wb, wsAccounts, 'Accounts Summary');

  // Save File
  const dateStr = format(new Date(), 'yyyyMMdd_HHmm');
  const filename = `${filenamePrefix}_${dateStr}.xlsx`;
  XLSX.writeFile(wb, filename);
};

/**
 * Export Accounts and Ledger Entries to a styled PDF document (.pdf)
 */
export const exportLedgerToPdf = (
  accounts: Account[],
  entries: LedgerEntry[],
  accountMap: Map<string, Account>,
  reportTitle: string = 'FULL LEDGER STATEMENT',
  filenamePrefix: string = 'Full_Ledger'
) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  const totalCredit = entries.reduce(
    (sum, entry) => (entry.entryType === 'credit' ? sum + entry.amount : sum),
    0
  );
  const totalDebit = entries.reduce(
    (sum, entry) => (entry.entryType === 'debit' ? sum + entry.amount : sum),
    0
  );
  const netBalance = totalCredit - totalDebit;
  const generatedOn = format(new Date(), 'dd MMM yyyy, hh:mm a');

  // Header Box
  doc.setFillColor(26, 35, 126); // Dark primary blue
  doc.rect(0, 0, 297, 28, 'F');

  // Header Title
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('MARUTI FIRE SYSTEM', 14, 12);

  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(reportTitle, 14, 20);

  doc.setFontSize(9);
  doc.text(`Generated: ${generatedOn}`, 283, 12, { align: 'right' });
  doc.text(`Total Accounts: ${accounts.length} | Total Entries: ${entries.length}`, 283, 20, { align: 'right' });

  // Summary Cards Bar
  doc.setFillColor(245, 247, 250);
  doc.roundedRect(14, 32, 269, 16, 2, 2, 'F');

  doc.setTextColor(33, 33, 33);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');

  doc.text('Total Credit:', 20, 42);
  doc.setTextColor(46, 125, 50); // Green
  doc.text(`Rs ${formatMoney(totalCredit)}`, 42, 42);

  doc.setTextColor(33, 33, 33);
  doc.text('Total Debit:', 95, 42);
  doc.setTextColor(198, 40, 40); // Red
  doc.text(`Rs ${formatMoney(totalDebit)}`, 116, 42);

  doc.setTextColor(33, 33, 33);
  doc.text('Net Balance:', 170, 42);
  doc.setTextColor(netBalance >= 0 ? 46 : 198, netBalance >= 0 ? 125 : 40, netBalance >= 0 ? 50 : 40);
  doc.text(`Rs ${formatMoney(netBalance)}`, 193, 42);

  doc.setTextColor(100, 100, 100);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(`(Includes ${entries.length} transactions)`, 245, 42);

  // Table Data preparation
  const tableHead = [
    ['#', 'Date', 'Account Name', 'Type', 'Entry', 'Category', 'Mode', 'Ref No', 'Amount (Rs)', 'Notes'],
  ];

  const tableData = entries.map((entry, index) => {
    const account = accountMap.get(entry.accountId);
    return [
      (index + 1).toString(),
      formatDateStr(entry.entryDate),
      account?.accountName || 'N/A',
      account?.accountType || 'N/A',
      entry.entryType.toUpperCase(),
      entry.category,
      entry.paymentMode,
      entry.referenceNo || '-',
      `Rs ${formatMoney(entry.amount)}`,
      entry.notes || '-',
    ];
  });

  autoTable(doc, {
    startY: 52,
    head: tableHead,
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: [30, 41, 59],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 8.5,
      halign: 'left',
    },
    bodyStyles: {
      fontSize: 8,
      textColor: [40, 40, 40],
    },
    columnStyles: {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 22 },
      2: { cellWidth: 45 },
      3: { cellWidth: 22 },
      4: { cellWidth: 18, fontStyle: 'bold' },
      5: { cellWidth: 25 },
      6: { cellWidth: 25 },
      7: { cellWidth: 25 },
      8: { cellWidth: 30, halign: 'right', fontStyle: 'bold' },
      9: { cellWidth: 47 },
    },
    didParseCell: (data: any) => {
      if (data.section === 'body' && data.column.index === 4) {
        if (data.cell.raw === 'CREDIT') {
          data.cell.styles.textColor = [46, 125, 50];
        } else if (data.cell.raw === 'DEBIT') {
          data.cell.styles.textColor = [198, 40, 40];
        }
      }
    },
    didDrawPage: (data: any) => {
      const pageCount = (doc as any).internal.getNumberOfPages();
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Maruti Fire System - Page ${data.pageNumber} of ${pageCount}`,
        283,
        202,
        { align: 'right' }
      );
    },
  });

  const dateStr = format(new Date(), 'yyyyMMdd_HHmm');
  const filename = `${filenamePrefix}_${dateStr}.pdf`;
  doc.save(filename);
};
